import React, { useState } from "react";
import "../../kds.css";
import "./leftbarkds.css";
import TimedOrderCard, { OrderCountdown } from "../TimedOrderCard";
import KdsTableReference from "../KdsTableReference";
import { formatPrepEstimate } from "../../orderTiming";
import { sortItemsByDue } from "../../itemPreparation";
import { ServingGroupHeading } from "../ItemPreparation";
import { CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import zomato from "../../../../../assets/images/zomato.png";
import swiggy from "../../../../../assets/images/swiggy.png";
import whatsapp from "../../../../../assets/images/whatsapp.png";
import websiteLogo from "../../../../../assets/images/cutlery.png";

const PLATFORM_CONFIG = {
  ZOMATO_ORDER: { icon: zomato, label: "Zomato", color: "#E23744" },
  SWIGGY_ORDER: { icon: swiggy, label: "Swiggy", color: "#FC8019" },
  WHATSAPP_ORDER: { icon: whatsapp, label: "WhatsApp", color: "#25D366" },
  B2C_ORDER: { icon: websiteLogo, label: "Website Order", color: "#0860E6" },
};
const externalSources = new Set(["ZOMATO_ORDER", "SWIGGY_ORDER", "WHATSAPP_ORDER", "UBER_EATS_ORDER", "B2C_ORDER"]);

function LeftBarKds({
  orders = [],
  scrollerRef,
  totalPendingResults,
  pendingLoading = false,
  error = "",
  onRetry,
  onAcceptOrders,
  onRejectOrders,
  actionBusy = false,
  allowAccept = true,
  allowReject = true,
  collapsed = false,
  onToggle,
}) {
  const visibleOrders = (Array.isArray(orders) ? orders : []).filter((order) => {
    const items = order?.items || [];
    return items.some((item) => item?.taskStatus !== "COMPLETED" && item?.taskStatus !== "REMOVED") || items.length === 0;
  });
  // Which button started the in-flight decision ("all" or an order id), so only it shows a spinner.
  const [activeDecision, setActiveDecision] = useState(null);
  const decide = async (handler, order) => {
    if (actionBusy || typeof handler !== "function") return;
    setActiveDecision(order.id);
    try { await handler([order.id]); } catch (_) { /* Shared decision hook reports failure. */ }
    finally { setActiveDecision(null); }
  };
  const acceptAll = async () => {
    if (actionBusy || !orders.length || typeof onAcceptOrders !== "function") return;
    setActiveDecision("all");
    try { await onAcceptOrders(orders.map((order) => order.id)); } catch (_) { /* Hook reports failure. */ }
    finally { setActiveDecision(null); }
  };
  const spinner = <span className="loader kds-btn-loader" aria-hidden="true" />;

  return (
    <aside className={`left-bar-kds${collapsed ? " is-collapsed" : ""}`} aria-label="Pending orders">
      <div className="leftbar-header">
        <div className="leftbar-header-left">
          <span className="leftbar-dot" aria-hidden="true" />
          <h1 className="leftbar-title">Pending</h1>
          <span className="leftbar-count">{totalPendingResults ?? visibleOrders.length}</span>
        </div>
        <button className="pending-collapse-toggle" type="button" onClick={onToggle}
          aria-expanded={!collapsed} aria-controls="kds-pending-order-content"
          aria-label={collapsed ? "Expand pending orders" : "Collapse pending orders"}>
          <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
            <path d={collapsed ? "m7 4 6 6-6 6" : "m13 4-6 6 6 6"} />
          </svg>
        </button>
      </div>

      {collapsed && (
        <ol className="pending-order-numbers" aria-label="Pending order numbers" aria-busy={pendingLoading}>
          {!error && visibleOrders.map((order) => (
            <li key={order.id} className="pending-order-number"
              aria-label={`Pending order ${order?.token || order?.orderNo || order.id}`}>
              {order?.token || order?.orderNo || order.id}
            </li>
          ))}
          {error && <li className="pending-number-state" role="alert" aria-label="Pending orders unavailable">!</li>}
          {!error && !visibleOrders.length && <li className="pending-number-state" aria-label={pendingLoading ? "Loading pending orders" : "No pending orders"}>—</li>}
        </ol>
      )}
      <div id="kds-pending-order-content" className="leftbar-content"
        hidden={collapsed} aria-hidden={collapsed} inert={collapsed ? "" : undefined}>
        <button className="accept-all-btn" type="button" onClick={acceptAll}
          disabled={actionBusy || !allowAccept || orders.length === 0} aria-busy={actionBusy}>
          {actionBusy && spinner}
          {actionBusy ? "Updating…" : `Accept all (${orders.length})`}
        </button>
        <div className="orders-container" ref={scrollerRef} aria-busy={pendingLoading || actionBusy}
          role="region" aria-label="Pending order cards" tabIndex={0}>
        {error ? (
          <div className="no-orders-parent-container" role="alert">
            <p className="no-orders">Pending orders could not be loaded.</p>
            {typeof onRetry === "function" && (
              <button type="button" onClick={onRetry} disabled={pendingLoading} aria-busy={pendingLoading}>
                {pendingLoading && spinner}
                {pendingLoading ? "Retrying…" : "Try again"}
              </button>
            )}
          </div>
        ) : visibleOrders.length ? visibleOrders.map((order) => {
          const platform = PLATFORM_CONFIG[order?.orderType];
          const preparationItems = sortItemsByDue(order, order?.items || []);
          return (
            <TimedOrderCard key={order.id} order={order} className="order-card kds-pending-card white-card">
              <div className="order-header-flex">
                <div className="order-header-left">
                  <p className="order-token">{order?.token || `#${order?.orderNo || ""}`}</p>
                  {externalSources.has(order?.orderType) && platform && (
                    <div className="platform-badge" style={{ color: platform.color }}>
                      <img src={platform.icon} alt="" className="platform-icon" />
                      <span>{platform.label}</span>
                    </div>
                  )}
                  {!externalSources.has(order?.orderType) && order?.metadata?.tableNumber && (
                    <KdsTableReference
                      tableNumber={order?.metadata?.tableNumber}
                      showPrefix
                    />
                  )}
                </div>
                <div className="order-header-right">
                  <p className="order-time">Prep: {formatPrepEstimate(order?.prepTime)}</p>
                  <p className="item-count">{order?.qty ?? order?.items?.length ?? 0} items</p>
                </div>
              </div>
              {!!order?.items?.length && (
                <div className="order-items-preview">
                  <div className="order-items-space">
                    {preparationItems.map((item, index) => (
                      <div key={item?.taskId || item?.id || index} className="order-item-preview-wrap">
                        <ServingGroupHeading order={order} item={item} items={preparationItems} index={index} />
                        <div className="order-item-preview-row">
                          <span className="order-item-preview-name" title={item?.name || "Unnamed item"}>
                            {item?.name || "Unnamed item"}
                            {item?.metadata?.isBundle && <span className="kds-combo-pill kds-combo-pill-inline">Combo</span>}
                          </span>
                          <span className="order-item-preview-qty">×{item?.qty ?? 0}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <OrderCountdown order={order} showDescription={false} />
              <div className="order-footer">
                <button type="button" className="reject-btn-left-bar" onClick={() => decide(onRejectOrders, order)}
                  disabled={actionBusy || !allowReject || !onRejectOrders} aria-label={`Reject order ${order?.token || order.id}`}>
                  <XMarkIcon className="btn-icon" /><span>Reject</span>
                </button>
                <button type="button" className="accept-btn-left-bar" onClick={() => decide(onAcceptOrders, order)}
                  disabled={actionBusy || !allowAccept || !onAcceptOrders} aria-label={`Accept order ${order?.token || order.id}`}
                  aria-busy={actionBusy && activeDecision === order.id}>
                  {actionBusy && activeDecision === order.id ? spinner : <CheckCircleIcon className="btn-icon" />}
                  <span>Accept</span>
                </button>
              </div>
            </TimedOrderCard>
          );
        }) : (
          <div className="no-orders-parent-container" role="status">
            <p className="no-orders">{pendingLoading ? "Loading pending orders…" : "No pending orders"}</p>
          </div>
        )}
        {pendingLoading && visibleOrders.length > 0 && <div className="loading-more" role="status">Refreshing orders…</div>}
        </div>
      </div>
    </aside>
  );
}

export default LeftBarKds;
